import React, { useState, useEffect, useMemo } from "react";
import "./TrainingLearning.scss";
import { Breadcrumb } from "components";
import { useRef } from "react";
import { t } from "react-switch-lang";
import { uploadToBunnyTus } from "./uploadToBunnyTus";
import api from "components/api/api";
import {
  Search,
  Filter,
  Plus,
  X,
  Upload,
  Eye,
  Users,
  Calendar,
  PlayCircle,
  FileText,
  CheckCircle,
} from "lucide-react";
import { Edit, Trash2 } from "react-feather";
import { getVideoDuration } from "./getVideoDuration";
const TrainingLearning = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedRegion, setSelectedRegion] = useState("all");
  const [isFileReplaced, setIsFileReplaced] = useState(false);

  const [showUploadModal, setShowUploadModal] = useState(false);
  const [loading, setLoading] = useState(false);
  // const [maxWatchedTime, setMaxWatchedTime] = useState({});
  const [materialsLoading, setMaterialsLoading] = useState(true);
  const [editingMaterial, setEditingMaterial] = useState(null);

  const userRole = (localStorage.getItem("userRole") || "").toLowerCase();
  const userId = localStorage.getItem("userId") || "";
  const iframeRef = useRef(null);
  const lastTickRef = useRef(Date.now());
  const progressQueueRef = useRef({});

  // Training materials state
  const [trainingMaterials, setTrainingMaterials] = useState([]);
  const [videoEngagement, setVideoEngagement] = useState([]);

  // Upload state
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [trainingTitle, setTrainingTitle] = useState("");
  const [dueDate, setDueDate] = useState("");
  const watchTimerRef = useRef(null);
  const watchedSecondsRef = useRef(0);
  const [mediaData, setMediaData] = useState(null);

  // Region/User state
  const [regionData, setRegionData] = useState({});
  const [regionNames, setRegionNames] = useState([]);
  const [assignSelectedRegion, setAssignSelectedRegion] =
    useState("All Regions");
  const [selectedDistrict, setSelectedDistrict] = useState("All Outlet");
  const [districts, setDistricts] = useState([]);
  const [userList, setUserList] = useState([]);
  const [selectedLearners, setSelectedLearners] = useState([]);
  const [assignSearchQuery, setAssignSearchQuery] = useState("");

  // Video viewing state (for non-admin users)
  const [viewingVideo, setViewingVideo] = useState(null);
  const [currentProgress, setCurrentProgress] = useState({});

  const [error, setError] = useState(null);
  const lastMilestoneRef = useRef({});

  const categories = ["all", "video", "document"];
  const isAdmin = userRole === "admin";
  const MILESTONES = [10, 25, 50, 75, 100];

  // Progress milestones to trigger API calls

  const getOriginalFileName = (publicId, format) => {
    if (!publicId) return "";
    return `${publicId}.${format}`;
  };

  // Fetch training materials
  const fetchTrainingMaterials = async () => {
    setMaterialsLoading(true);
    try {
      const response = await api.fetchAllTrainings();
      setTrainingMaterials(response.data.data || []);
    } catch (error) {
      console.error("Failed to fetch training materials:", error);
      setError("Failed to load training materials");
    } finally {
      setMaterialsLoading(false);
    }
  };
  const getDurationSecondsFromText = (text) => {
    if (!text) return 0;

    // "9 sec"
    if (text.includes("sec") && !text.includes("min")) {
      return parseInt(text, 10);
    }

    // "1 min 20 sec"
    const minMatch = text.match(/(\d+)\s*min/);
    const secMatch = text.match(/(\d+)\s*sec/);

    const minutes = minMatch ? parseInt(minMatch[1], 10) : 0;
    const seconds = secMatch ? parseInt(secMatch[1], 10) : 0;

    return minutes * 60 + seconds;
  };

  useEffect(() => {
    if (!viewingVideo) return;

    lastTickRef.current = Date.now();
    watchedSecondsRef.current = 0;

    watchTimerRef.current = setInterval(() => {
      if (document.visibilityState !== "visible") return;

      const now = Date.now();
      const delta = Math.floor((now - lastTickRef.current) / 1000);
      lastTickRef.current = now;

      if (delta <= 0) return;

      watchedSecondsRef.current += delta;

      const duration =
        mediaData?.durationSeconds ||
        getDurationSecondsFromText(viewingVideo.duration);

      if (!duration) return;

      processProgress(watchedSecondsRef.current, duration, viewingVideo.id);
    }, 1000);

    return () => clearInterval(watchTimerRef.current);
  }, [viewingVideo]);

  const persistProgress = async (trainingId, progress) => {
    // prevent duplicate inflight calls
    if (progressQueueRef.current[trainingId]) return;

    progressQueueRef.current[trainingId] = true;

    try {
      await api.updateTrainingProgress({
        userId,
        trainingId,
        progress,
        status:
          progress === 100
            ? "completed"
            : progress > 0
            ? "in-progress"
            : "not-started",
      });

      lastMilestoneRef.current[trainingId] = progress;
    } catch (err) {
      console.error("Progress update failed", err);
    } finally {
      delete progressQueueRef.current[trainingId];
    }
  };

  useEffect(() => {
    if (!viewingVideo) return;

    const savedProgress = currentProgress[viewingVideo.id] || 0;
    const duration =
      mediaData?.durationSeconds ||
      getDurationSecondsFromText(viewingVideo.duration);

    watchedSecondsRef.current = Math.floor((savedProgress / 100) * duration);
  }, [viewingVideo]);

  const playerUrl = viewingVideo
    ? `${
        viewingVideo.videoPlaybackUrl
      }?player_api=1&enablejsapi=1&origin=${encodeURIComponent(
        window.location.origin
      )}`
    : "";

  const fetchUserTrainingMaterials = async () => {
    if (!userId) return;

    setMaterialsLoading(true);
    try {
      const res = await api.fetchUserTrainings(userId);

      const progressMap = {};

      const mappedMaterials = (res.data.data || []).map((item) => {
        progressMap[item.trainingId] = item.progress || 0;

        return {
          id: item.trainingId,
          title: item.title,
          type: item.type,
          videoPlaybackUrl: item.videoPlaybackUrl,
          documentUrl: item.documentUrl,
          duration: item.duration,
          dueDate: item.dueDate,
          region: "assigned",
          uploadDate: item.createdAt,
        };
      });

      setTrainingMaterials(mappedMaterials);
      setCurrentProgress(progressMap);
    } catch (error) {
      console.error("Failed to fetch user trainings:", error);
      setError("Failed to load assigned trainings");
    } finally {
      setMaterialsLoading(false);
    }
  };

  const processProgress = (watched, duration, trainingId) => {
    const progress = Math.min(100, Math.floor((watched / duration) * 100));

    // UI update only
    setCurrentProgress((prev) => ({
      ...prev,
      [trainingId]: progress,
    }));

    const lastSaved = lastMilestoneRef.current[trainingId] || 0;

    const milestone = MILESTONES.find((m) => progress >= m && lastSaved < m);

    if (!milestone) return;

    // Anti-skip validation
    if (watched < (duration * milestone) / 100 - 5) return;

    persistProgress(trainingId, milestone);
  };

  useEffect(() => {
    const initialize = async () => {
      if (isAdmin) {
        await fetchTrainingMaterials();
        await fetchLearners();
        await fetchVideoEngagement();
      } else {
        await fetchUserTrainingMaterials();
      }
    };

    initialize();
  }, [isAdmin, userId]);

  const handleDeleteTraining = async (materialId) => {
    if (!window.confirm("Are you sure you want to delete this training?"))
      return;

    try {
      await api.deleteTraining(materialId);
      if (isAdmin) {
        await fetchTrainingMaterials();
      } else {
        await fetchUserTrainingMaterials();
      }
      alert("Training deleted successfully");
    } catch (err) {
      console.error("Delete failed", err);
      alert("Failed to delete training");
    }
  };

  const handleEditTraining = async (material) => {
    try {
      setLoading(true);

      const res = await api.fetchTrainingById(material.id);
      const data = res.data.data;

      // 🔹 keep full backend DTO
      setEditingMaterial(data);

      // 🔹 form fields
      setTrainingTitle(data.title || "");

      // dueDate not in DTO → keep existing or default
      setDueDate(
        data.uploadDate
          ? new Date(data.uploadDate).toISOString().split("T")[0]
          : ""
      );

      setAssignSelectedRegion(
        data.region === "all"
          ? "All Regions"
          : data.region.charAt(0).toUpperCase() + data.region.slice(1)
      );

      // 🔹 preselect assigned users
      setSelectedLearners(data.assignedUserIds || []);

      // 🔹 prefill existing media (IMPORTANT)
      if (data.type === "video") {
        setMediaData({
          type: "video",
          videoPlaybackUrl: data.videoPlaybackUrl,
          videoPublicId: data.videoPublicId,
          videoCloudResourceType: data.videoCloudResourceType,
          videoFormat: data.videoFormat,
          duration: data.duration,
        });
      } else {
        setMediaData({
          type: "document",
          documentUrl: data.documentUrl,
        });
      }

      setShowUploadModal(true);
    } catch (err) {
      console.error("Failed to fetch training", err);
      alert("Failed to load training for edit");
    } finally {
      setLoading(false);
    }
  };

  // Fetch learners
  const fetchLearners = async () => {
    try {
      const res = await api.fetchUsers();
      const users = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.data)
        ? res.data.data
        : Array.isArray(res.data?.users)
        ? res.data.users
        : [];

      const filteredUsers = users.filter((u) => u.role !== "ADMIN");
      const regionMap = { "All Regions": {} };

      filteredUsers.forEach((user) => {
        const { id, username, region, outlet } = user;
        if (!region || !outlet) return;

        if (!regionMap[region]) regionMap[region] = {};
        if (!regionMap[region][outlet]) regionMap[region][outlet] = [];

        regionMap[region][outlet].push({
          id,
          name: username,
          region,
          outlet,
        });
      });

      setRegionData(regionMap);
      setRegionNames(Object.keys(regionMap));
    } catch (err) {
      console.error("Failed to fetch learners", err);
    }
  };

  // Fetch video engagement
  const fetchVideoEngagement = async () => {
    try {
      const res = await api.fetchVideoEngagement();
      setVideoEngagement(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (error) {
      console.error("Failed to fetch video engagement:", error);
    }
  };

  // Initialize users when regionData loads
  useEffect(() => {
    if (Object.keys(regionData).length === 0) return;

    const allUsers = Object.values(regionData)
      .flatMap((r) => Object.values(r || {}))
      .flat();

    setUserList(allUsers);
    setSelectedLearners(allUsers.map((u) => u.id));
  }, [regionData]);

  // Set default due date (7 days from now)
  useEffect(() => {
    const defaultDue = new Date();
    defaultDue.setDate(defaultDue.getDate() + 7);
    setDueDate(defaultDue.toISOString().split("T")[0]);
  }, []);

  const handleRegionChange = (region) => {
    setAssignSelectedRegion(region);

    if (region === "All Regions") {
      const allDistricts = Object.values(regionData).flatMap((r) =>
        Object.keys(r || {})
      );
      const allUsers = Object.values(regionData)
        .flatMap((r) => Object.values(r || {}))
        .flat();

      setDistricts(allDistricts);
      setSelectedDistrict("All Outlet");
      setUserList(allUsers);
      setSelectedLearners(allUsers.map((u) => u.id));
    } else {
      const regionDistricts = Object.keys(regionData[region] || {});
      const usersInRegion = Object.values(regionData[region] || {}).flat();

      setDistricts(regionDistricts);
      setSelectedDistrict("All Outlet");
      setUserList(usersInRegion);
      setSelectedLearners(usersInRegion.map((u) => u.id));
    }
  };

  const getMaterialProgress = (materialId) => {
    // For non-admin users, return their own progress
    if (!isAdmin) {
      return currentProgress[materialId] || 0;
    }

    // For admin, return average progress
    const engagements = videoEngagement.filter(
      (e) => e.trainingId === materialId
    );

    if (engagements.length === 0) return 0;

    const avg =
      engagements.reduce((sum, e) => sum + (e.progress || 0), 0) /
      engagements.length;

    return Math.round(avg);
  };

  const handleDistrictChange = (district) => {
    setSelectedDistrict(district);

    if (district === "All Outlet") {
      const users =
        assignSelectedRegion === "All Regions"
          ? Object.values(regionData)
              .flatMap((r) => Object.values(r || {}))
              .flat()
          : Object.values(regionData[assignSelectedRegion] || {}).flat();

      setUserList(users);
      setSelectedLearners(users.map((u) => u.id));
    } else {
      const users =
        assignSelectedRegion === "All Regions"
          ? Object.values(regionData)
              .flatMap((r) => r[district] || [])
              .flat()
          : regionData[assignSelectedRegion]?.[district] || [];

      setUserList(users);
      setSelectedLearners(users.map((u) => u.id));
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadedFile(file);
    setIsFileReplaced(true);

    if (!trainingTitle.trim()) {
      alert("Please enter training title first");
      return;
    }

    const isVideo = file.type.startsWith("video/");
    const isDocument = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-powerpoint",
      "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    ].includes(file.type);

    setIsUploading(true);
    setUploadProgress(0);

    try {
      // ================= VIDEO =================
      if (isVideo) {
        const durationSeconds = await getVideoDuration(file);

        const formattedDuration =
          durationSeconds < 60
            ? `${durationSeconds} sec`
            : `${Math.floor(durationSeconds / 60)} min ${
                durationSeconds % 60
              } sec`;

        const createRes = await api.createBunnyVideo(trainingTitle);
        const { videoId } = createRes.data;

        const sigRes = await api.getTusSignature(videoId);
        const { signature, expires, libraryId } = sigRes.data;

        const result = await uploadToBunnyTus({
          file,
          videoId,
          libraryId,
          signature,
          expires,
          title: trainingTitle,
          onProgress: setUploadProgress,
        });

        setMediaData({
          ...result,
          type: "video",
          duration: formattedDuration,
          durationSeconds,
        });
      }

      // ================= DOCUMENT =================
      else if (isDocument) {
        const formData = new FormData();
        formData.append("file", file);

        const res = await api.uploadDocument(formData);

        setMediaData({
          type: "document",
          documentUrl: res.data.documentUrl,
          format: file.name.split(".").pop(),
        });
      } else {
        alert("Unsupported file type");
      }
    } catch (err) {
      console.error(err);
      alert("Upload failed");
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (!trainingTitle.trim()) return alert("Enter title");
    if (!editingMaterial && !mediaData) return alert("Upload a file");
    if (selectedLearners.length === 0) return alert("Select learners");

    setLoading(true);

    try {
      const payload = {
        material: {
          title: trainingTitle,
          region:
            assignSelectedRegion === "All Regions"
              ? "all"
              : assignSelectedRegion.toLowerCase(),
          type: mediaData.type,
          duration: mediaData.type === "video" ? mediaData.duration : null,
        },

        ...(mediaData.type === "video" && {
          videoPublicId: mediaData.videoPublicId,
          videoPlaybackUrl: mediaData.videoPlaybackUrl,
          videoFormat: mediaData.videoFormat,
        }),

        ...(mediaData.type === "document" && {
          documentUrl: mediaData.documentUrl,
        }),

        userIds: selectedLearners,
        dueDate: new Date(dueDate).toISOString(),
      };

      if (editingMaterial) {
        await api.updateTraining(editingMaterial.id, payload);
        alert("Training updated successfully");
      } else {
        await api.uploadTraining(payload);
        alert("Training uploaded successfully");
      }

      await fetchTrainingMaterials();
      resetModal();
      setEditingMaterial(null);
    } catch (err) {
      console.error(err);
      alert("Operation failed");
    } finally {
      setLoading(false);
    }
  };

  const toggleLearnerSelection = (learnerId) => {
    setSelectedLearners((prev) => {
      if (prev.includes(learnerId)) {
        return prev.filter((id) => id !== learnerId);
      }
      return [...prev, learnerId];
    });
  };

  // Update progress in backend (only at milestones)
  const updateProgressInBackend = async (trainingId, progress) => {
    try {
      await api.updateTrainingProgress({
        userId,
        trainingId,
        progress,
        status:
          progress >= 100
            ? "completed"
            : progress > 0
            ? "in-progress"
            : "not-started",
      });

      console.log(`Progress saved at ${progress}% for training ${trainingId}`);

      // Update last saved progress
    } catch (error) {
      console.error("Failed to update progress:", error);
    }
  };

  // View material with video tracking for non-admin users
  const viewMaterial = (material) => {
    if (material.type === "video") {
      if (!isAdmin) {
        setViewingVideo(material);
        return;
      }
      window.open(material.videoPlaybackUrl, "_blank");
    } else {
      const current = currentProgress[material.id] || 0;

      if (current < 100) {
        updateProgressInBackend(material.id, 100);

        setCurrentProgress((prev) => ({
          ...prev,
          [material.id]: 100,
        }));
      }

      window.open(material.documentUrl, "_blank");
    }
  };

  // Close video modal
  const closeVideoModal = () => {
    clearInterval(watchTimerRef.current);
    setViewingVideo(null);
  };

  const resetModal = () => {
    setShowUploadModal(false);
    setTrainingTitle("");
    setUploadedFile(null);
    setUploadProgress(0);
    setMediaData(null);
    setIsFileReplaced(false);
    setSelectedLearners([]);
    setAssignSelectedRegion("All Regions");
    setSelectedDistrict("All Outlet");
    setError(null);
    setIsUploading(false);
  };

  // Filtered materials
  const filteredMaterials = useMemo(() => {
    return trainingMaterials.filter((material) => {
      const matchesSearch =
        material.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        material.description?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory =
        selectedCategory === "all" || material.type === selectedCategory;
      const matchesRegion =
        selectedRegion === "all" ||
        material.region === selectedRegion ||
        material.region === "all";

      return matchesSearch && matchesCategory && matchesRegion;
    });
  }, [searchQuery, selectedCategory, selectedRegion, trainingMaterials]);

  // Filtered users for assign
  const filteredUserList = useMemo(() => {
    if (!assignSearchQuery.trim()) return userList;
    return userList.filter((user) =>
      user.name.toLowerCase().includes(assignSearchQuery.toLowerCase())
    );
  }, [userList, assignSearchQuery]);

  // Calculate stats
  const getStats = () => {
    const totalMaterials = trainingMaterials.length;
    const totalAssignments = trainingMaterials.reduce(
      (sum, m) => sum + (m.assignedTo || 0),
      0
    );
    const completedVideos = videoEngagement.filter(
      (e) => e.status === "completed"
    ).length;
    const avgCompletion = Math.round(
      videoEngagement.reduce((sum, e) => sum + e.progress, 0) /
        (videoEngagement.length || 1)
    );
    return { totalMaterials, totalAssignments, completedVideos, avgCompletion };
  };

  const stats = getStats();

  if (materialsLoading) {
    return (
      <div className="training-learning">
        <Breadcrumb title={t("Training & Learning")} />
        <div style={{ textAlign: "center", padding: "3rem" }}>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="training-learning offers-container">
      <Breadcrumb title={t("Training & Learning")} />

      <div className="offers-header">
        <div className="header-content">
          <div className="header-title-row">
            <div>
              <p className="page-subtitle">
                {isAdmin
                  ? "Upload, Assign & Track Learning Materials"
                  : "Access your training materials and track progress"}
              </p>
            </div>
            {isAdmin && (
              <button
                className="create-btn"
                onClick={() => setShowUploadModal(true)}
              >
                <Plus size={20} />
                Upload
              </button>
            )}
          </div>
        </div>

        <div className="header-stats">
          <div className="stat-card">
            <span className="stat-value">{stats.totalMaterials}</span>
            <span className="stat-label">Training Materials</span>
          </div>
          {isAdmin && (
            <>
              <div className="stat-card">
                <span className="stat-value">{stats.totalAssignments}</span>
                <span className="stat-label">Total Assignments</span>
              </div>
              <div className="stat-card">
                <span className="stat-value">{stats.completedVideos}</span>
                <span className="stat-label">Completed</span>
              </div>
              <div className="stat-card">
                <span className="stat-value">{stats.avgCompletion}%</span>
                <span className="stat-label">Avg Completion</span>
              </div>
            </>
          )}
        </div>
      </div>

      {error && (
        <div
          style={{
            padding: "1rem",
            margin: "1rem 0",
            backgroundColor: "#fee",
            color: "#c33",
            borderRadius: "4px",
          }}
        >
          {error}
        </div>
      )}

      <div className="filters-section">
        <div className="search-box">
          <Search className="search-icon" size={20} />
          <input
            type="text"
            placeholder="Search training materials..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filter-group">
          <div className="filter-label">
            <Filter size={16} />
            <span>Type</span>
          </div>
          <div className="filter-buttons">
            {categories.map((category) => (
              <button
                key={category}
                className={`filter-btn ${
                  selectedCategory === category ? "active" : ""
                }`}
                onClick={() => setSelectedCategory(category)}
              >
                {category === "all" ? "All" : category}
              </button>
            ))}
          </div>
        </div>
        {isAdmin && (
          <div className="filter-group">
            <div className="filter-label">
              <Filter size={16} />
              <span>Region</span>
            </div>
            <div className="filter-buttons">
              <button
                className={`filter-btn ${
                  selectedRegion === "all" ? "active" : ""
                }`}
                onClick={() => setSelectedRegion("all")}
              >
                All Regions
              </button>
              {regionNames
                .filter((r) => r !== "All Regions")
                .map((region) => (
                  <button
                    key={region}
                    className={`filter-btn ${
                      selectedRegion === region ? "active" : ""
                    }`}
                    onClick={() => setSelectedRegion(region)}
                  >
                    {region}
                  </button>
                ))}
            </div>
          </div>
        )}
      </div>

      <div className="offers-grid">
        {filteredMaterials.length > 0 ? (
          filteredMaterials.map((material) => (
            <div key={material.id} className="offer-card">
              <div className="offer-header-badge">
                <div className="category-badge">
                  {material.type === "video" ? (
                    <PlayCircle size={18} />
                  ) : (
                    <FileText size={18} />
                  )}
                  <span>{material.type}</span>
                </div>
                <div className="region-badge">
                  {!isAdmin && getMaterialProgress(material.id) >= 100
                    ? "Completed"
                    : material.region}
                </div>
              </div>

              <h3 className="offer-title">{material.title}</h3>
              {material.description && (
                <p className="offer-description">{material.description}</p>
              )}

              {isAdmin ? (
                <div className="admin-meta">
                  <div className="meta-item">
                    <Users size={14} />
                    <span>{material.assignedTo || 0} assigned</span>
                  </div>
                  <div className="meta-item">
                    <span>{material.completionRate || 0}% completed</span>
                  </div>
                </div>
              ) : (
                // Progress bar for regular users (video only)
                material.type === "video" && (
                  <div style={{ marginTop: "1rem", marginBottom: "1rem" }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginBottom: "0.5rem",
                        fontSize: "0.85rem",
                        color: "#7f8c8d",
                      }}
                    >
                      <span>Progress</span>
                      <span style={{ fontWeight: "600", color: "#2c3e50" }}>
                        {getMaterialProgress(material.id)}%
                      </span>
                    </div>
                    <div
                      style={{
                        height: "8px",
                        backgroundColor: "#e9ecef",
                        borderRadius: "10px",
                        overflow: "hidden",
                        boxShadow: "inset 0 1px 3px rgba(0, 0, 0, 0.1)",
                      }}
                    >
                      <div
                        style={{
                          height: "100%",
                          width: `${getMaterialProgress(material.id)}%`,
                          backgroundColor:
                            getMaterialProgress(material.id) >= 100
                              ? "#2ecc71"
                              : getMaterialProgress(material.id) >= 50
                              ? "#3498db"
                              : "#f39c12",
                          borderRadius: "10px",
                          transition:
                            "width 0.3s ease, background-color 0.3s ease",
                          boxShadow: "0 0 10px rgba(52, 152, 219, 0.5)",
                        }}
                      />
                    </div>
                  </div>
                )
              )}

              <div className="offer-footer">
                <div className="offer-meta">
                  <Calendar size={14} />
                  <span>
                    {material.dueDate
                      ? new Date(material.dueDate).toLocaleDateString("en-GB", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                        })
                      : "N/A"}
                  </span>
                </div>
                {material.type === "video" && (
                  <div className="valid-until">⏱️ {material.duration}</div>
                )}
              </div>

              <button
                className="view-details-btn"
                onClick={() => viewMaterial(material)}
              >
                <Eye size={18} />
                View Material
              </button>

              {isAdmin && (
                <div className="admin-actions">
                  <button
                    className="icon-btn edit"
                    onClick={() => handleEditTraining(material)}
                    title="Edit"
                  >
                    <Edit size={16} />
                  </button>

                  <button
                    className="icon-btn delete"
                    onClick={() => handleDeleteTraining(material.id)}
                    title="Delete"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              )}
            </div>
          ))
        ) : (
          <div className="no-results">
            <Search size={48} />
            <h3>No training materials found</h3>
            <p>Try adjusting your search or filters</p>
          </div>
        )}
      </div>

      {/* Video Viewing Modal for Non-Admin Users */}
      {viewingVideo && !isAdmin && (
        <div className="modal-overlay" onClick={closeVideoModal}>
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "1000px" }}
          >
            <div className="modal-header-offer">
              <h2>{viewingVideo.title}</h2>
              <button className="close-btn" onClick={closeVideoModal}>
                <X size={24} />
              </button>
            </div>

            <div className="modal-body">
              {/* Video Player */}
              <iframe
                ref={iframeRef}
                src={playerUrl}
                allow="autoplay; encrypted-media; picture-in-picture"
                allowFullScreen
                style={{
                  width: "100%",
                  height: "70vh",
                  borderRadius: "8px",
                  border: "none",
                  backgroundColor: "#000",
                }}
              />

              {/* Completion Message */}
              {currentProgress[viewingVideo.id] >= 100 && (
                <div
                  style={{
                    marginTop: "1rem",
                    padding: "1rem",
                    backgroundColor: "#d4edda",
                    border: "1px solid #c3e6cb",
                    borderRadius: "8px",
                    color: "#155724",
                    textAlign: "center",
                    fontWeight: "600",
                  }}
                >
                  🎉 Congratulations! You've completed this training video!
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Single Step Upload & Assign Modal */}
      {showUploadModal && isAdmin && (
        <div className="modal-overlay" onClick={resetModal}>
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "800px" }}
          >
            <div className="modal-header-offer">
              <h2>Upload & Assign Training</h2>
              <button className="close-btn" onClick={resetModal}>
                <X size={24} />
              </button>
            </div>

            <div className="modal-body">
              {/* Training Details Section */}
              <div className="form-section">
                <div className="form-row">
                  <div className="form-group full-width">
                    <label>Training Title *</label>
                    <input
                      type="text"
                      value={trainingTitle}
                      onChange={(e) => setTrainingTitle(e.target.value)}
                      placeholder="Enter training title"
                    />
                  </div>
                </div>

                <div className="upload-area" style={{ marginTop: "1rem" }}>
                  <div className="upload-icon">
                    {mediaData ? (
                      <CheckCircle size={48} color="#2ecc71" />
                    ) : (
                      "📁"
                    )}
                  </div>
                  <p>
                    {uploadedFile && isFileReplaced && (
                      <>
                        Selected: <strong>{uploadedFile.name}</strong>
                      </>
                    )}

                    {!uploadedFile && editingMaterial && mediaData && (
                      <>
                        Current file:{" "}
                        <strong>
                          {getOriginalFileName(
                            mediaData.videoPublicId,
                            mediaData.videoFormat
                          )}
                        </strong>
                        <br />
                        <span style={{ fontSize: "0.85rem", color: "#7f8c8d" }}>
                          Upload a new file only if you want to replace it
                        </span>
                      </>
                    )}

                    {!uploadedFile && !editingMaterial && (
                      <>Drag & drop files here or click to browse</>
                    )}
                  </p>

                  {mediaData && (
                    <p
                      style={{
                        color: "#2ecc71",
                        fontWeight: "600",
                        marginTop: "0.5rem",
                      }}
                    >
                      ✓ File uploaded successfully
                    </p>
                  )}
                  <p
                    style={{
                      fontSize: "0.85rem",
                      color: "#7f8c8d",
                      marginTop: "0.5rem",
                    }}
                  >
                    Supported: Videos (MP4, WebM, MOV), Documents (PDF, DOC,
                    PPT, XLS)
                  </p>
                  <label
                    className="btn-primary"
                    style={{
                      cursor: isUploading ? "not-allowed" : "pointer",
                      marginTop: "1rem",
                    }}
                  >
                    <Upload size={18} />
                    {isUploading ? "Uploading..." : "Select File"}
                    <input
                      type="file"
                      style={{ display: "none" }}
                      onChange={handleFileSelect}
                      accept="video/*,.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx"
                      disabled={isUploading}
                    />
                  </label>
                  {uploadProgress > 0 && isUploading && (
                    <div className="progress-bar" style={{ marginTop: "1rem" }}>
                      <div
                        className="progress-fill"
                        style={{ width: `${uploadProgress}%` }}
                      ></div>
                      <span className="progress-text">{uploadProgress}%</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="form-group" style={{ marginTop: "1rem" }}>
                <label>Due Date *</label>
                <input
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  min={new Date().toISOString().split("T")[0]}
                />
              </div>

              {/* Assignment Section */}
              <div className="form-section" style={{ marginTop: "1rem" }}>
                <div className="form-row">
                  <div className="form-group">
                    <label>Region *</label>
                    <select
                      value={assignSelectedRegion}
                      onChange={(e) => handleRegionChange(e.target.value)}
                    >
                      {regionNames.map((region) => (
                        <option key={region} value={region}>
                          {region}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Outlet</label>
                    <select
                      value={selectedDistrict}
                      onChange={(e) => handleDistrictChange(e.target.value)}
                      disabled={assignSelectedRegion === "All Regions"}
                    >
                      <option value="All Outlet">All Outlet</option>
                      {districts.map((d) => (
                        <option key={d} value={d}>
                          {d}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>Search Learners</label>
                  <div className="search-box">
                    <Search className="search-icon" size={20} />
                    <input
                      type="text"
                      placeholder="Search learners..."
                      value={assignSearchQuery}
                      onChange={(e) => setAssignSearchQuery(e.target.value)}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: "0.5rem",
                    }}
                  >
                    <label style={{ cursor: "pointer", fontWeight: 600 }}>
                      <input
                        type="checkbox"
                        checked={
                          filteredUserList.length > 0 &&
                          selectedLearners.length === filteredUserList.length
                        }
                        onChange={() =>
                          setSelectedLearners(
                            selectedLearners.length === filteredUserList.length
                              ? []
                              : filteredUserList.map((u) => u.id)
                          )
                        }
                      />
                      Select All ({filteredUserList.length})
                    </label>
                    <span
                      style={{
                        color: "#2ecc71",
                        fontSize: "0.9rem",
                        fontWeight: "600",
                      }}
                    >
                      {selectedLearners.length} selected
                    </span>
                  </div>

                  <div
                    className="user-list"
                    style={{
                      maxHeight: "300px",
                      overflowY: "auto",
                      border: "1px solid #ecf0f1",
                      borderRadius: "8px",
                      padding: "0.5rem",
                    }}
                  >
                    {filteredUserList.map((user) => (
                      <label
                        key={user.id}
                        className="user-checkbox"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          padding: "0.75rem",
                          borderRadius: "6px",
                          cursor: "pointer",
                          transition: "background-color 0.2s",
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.backgroundColor = "#f8f9fa")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.backgroundColor =
                            "transparent")
                        }
                      >
                        <input
                          type="checkbox"
                          checked={selectedLearners.includes(user.id)}
                          onChange={() => toggleLearnerSelection(user.id)}
                          style={{ marginRight: "0.75rem" }}
                        />
                        <div>
                          <div style={{ fontWeight: "500" }}>{user.name}</div>
                          <div
                            style={{ fontSize: "0.85rem", color: "#7f8c8d" }}
                          >
                            {user.outlet} • {user.region}
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div className="modal-footer" style={{ marginTop: "2rem" }}>
                <button
                  className="cancel-btn"
                  onClick={resetModal}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  className="save-btn"
                  onClick={handleSubmit}
                  disabled={
                    !trainingTitle.trim() ||
                    selectedLearners.length === 0 ||
                    !dueDate ||
                    loading ||
                    isUploading
                  }
                  style={{
                    opacity:
                      !trainingTitle.trim() ||
                      selectedLearners.length === 0 ||
                      !dueDate ||
                      loading ||
                      isUploading
                        ? 0.6
                        : 1,
                    backgroundColor: "#2ecc71",
                  }}
                >
                  <CheckCircle size={18} />
                  {loading
                    ? "Submitting..."
                    : `Upload & Assign to ${selectedLearners.length} Learner(s)`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrainingLearning;
